import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { useTour } from "./context";

/* -----------------------------------------------------------------------------
 * TourOverlay — 13-step interactive tour aligned with the REAL recharge flow.
 *
 * Design principles:
 *   • Every step has BOTH a "Siguiente →" and "← Atrás" button. The user can
 *     always advance or go back at any moment; auto-advance is a courtesy
 *     when the user completes the action naturally (blur, click, etc.).
 *   • The verify step (5) is CONDITIONAL: if the game doesn't render a
 *     "Verificar" button, the step is auto-skipped after 1.2s.
 *   • The credentials step (4) adapts its copy at render time based on
 *     which credential inputs the current game renders (ID vs user/password).
 * --------------------------------------------------------------------------- */

const STEP_DEFS = [
  null, // 0 = inactive
  {
    key: "pick-game",
    // The whole game grid (any active card).
    targetSel: '[data-testid^="game-card-"]:not([aria-disabled="true"])',
    title: "Paso 1 · Selecciona el juego",
    body: null, // no body — keep it minimal per user request
    advanceOn: "game-click",
  },
  {
    key: "pick-package",
    targetSel: '[data-testid="package-selection-section"]',
    title: "Paso 2 · Elige el paquete",
    body: "Selecciona el paquete o pase que deseas comprar.",
    advanceOn: "package-select",
  },
  {
    key: "realizar-pago",
    targetSel: '[data-testid="realizar-pago-btn"]',
    title: 'Paso 3 · Toca "REALIZAR PAGO"',
    body: "Presiona el botón REALIZAR PAGO para continuar.",
    advanceOn: "realizar-pago-click",
  },
  {
    key: "credentials",
    targetSel: '[data-testid="player-id-input"], [data-testid="player-username-input"]',
    title: "Paso 4 · Coloca tus credenciales",
    // Body is set at render time based on the credential inputs present.
    body: null,
    advanceOn: "manual-only",
  },
  {
    key: "verify",
    targetSel: '[data-testid="verify-player-id-btn"]',
    title: "Paso 5 · Consulta tu ID",
    body: 'Toca "Consultar ID" para que el sistema busque tu cuenta y muestre tu nickname real antes de recargar.',
    advanceOn: "verify-click",
    conditional: true,
  },
  {
    key: "ff-confirm",
    targetSel: '[data-testid="ff-confirmation-box"]',
    title: "Paso 6 · Confirma que eres tú",
    body: 'Revisa el Nickname y la Región mostrados. Si son los correctos, toca "Sí, continuar al pago". Si no coinciden, toca "No, corregir ID" y verifica el número.',
    advanceOn: "ff-confirm-accept-click",
    conditional: true,
  },
  {
    key: "add-to-cart",
    targetSel: '[data-testid="add-to-cart-btn"]',
    title: "Paso 7 · Añade al carrito",
    body: 'Toca "Añadir al Carrito" para guardar el paquete en tu selección.',
    advanceOn: "add-to-cart-click",
  },
  {
    key: "open-cart",
    targetSel: '[data-testid="client-tab-cart"]',
    title: "Paso 8 · Presiona el Carrito",
    body: "Abre la pestaña Carrito para continuar con el pago.",
    advanceOn: "cart-tab-click",
  },
  {
    key: "checkout-btn",
    targetSel: '[data-testid="checkout-btn"]',
    title: "Paso 9 · Realizar compra",
    body: 'Toca "Proceder al pago".',
    advanceOn: "checkout-btn-click",
  },
  {
    key: "payment-method",
    targetSel: '[data-testid="checkout-pm-tabs"], [data-testid="payment-method-select"]',
    title: "Paso 10 · Elige el método de pago",
    body: "Toca a qué banco quieres realizar la recarga.",
    advanceOn: "payment-selected",
  },
  {
    key: "amount",
    targetSel: '[data-testid="paid-amount-input"]',
    title: "Paso 11 · Coloca el monto a pagar",
    body: "Escribe el monto exacto que pagaste (debe cubrir el total). Cuando termines, pulsa Siguiente.",
    advanceOn: "manual-only",
  },
  {
    key: "proof",
    targetSel: '[data-testid="proof-file-input"] , label[for="proof-file-input"]',
    title: "Paso 12 · Sube tu captura",
    body: "Toca y busca la captura de tu pago. La referencia bancaria se lee automáticamente del comprobante.",
    advanceOn: "proof-attached",
  },
  {
    key: "confirm",
    targetSel: '[data-testid="confirm-payment-submit-btn"]',
    title: "Paso 13 · Confirmar Pago",
    body: 'Toca "Confirmar y Enviar Pago" para procesar tu pedido.',
    advanceOn: "confirm-click",
  },
  {
    key: "finalized",
    targetSel: null,
    title: "¡Excelente! Pedido enviado",
    body: "Podrás ver el estado en tiempo real dentro de la sección Historial.",
    advanceOn: "terminal",
  },
];

const TOTAL_STEPS = STEP_DEFS.length - 2; // real numbered steps (excludes null-slot and terminal card)
const TERMINAL_STEP = STEP_DEFS.length - 1; // index of the "¡Excelente!" screen
const SPOT_PAD = 10;
const PULSE_CLASS = "ag7-tour-pulse";

// Detects credential type from the DOM: user/password games render a password
// input; games that need an ID render `player-id-input`. Returns:
//   'userpass'         → e.g. Roblox (username + password, no verify button)
//   'userpass_access'  → e.g. Call of Duty Mobile (user + password + access method picker)
//   'id'               → any game with an ID field (with or without verify button)
//   'unknown'          → neither field is rendered yet
function detectCredentialType() {
  if (typeof document === "undefined") return "unknown";
  const hasPass = !!document.querySelector('[data-testid="player-password-input"]');
  const hasId = !!document.querySelector('[data-testid="player-id-input"]');
  const hasAccessGroup = !!document.querySelector('[data-testid="access-method-group"]');
  if (hasPass && hasAccessGroup) return "userpass_access";
  if (hasPass) return "userpass";
  if (hasId) return "id";
  return "unknown";
}

export function TourOverlay() {
  const { step, active, next, back, skip, finish, goto } = useTour();
  const [rect, setRect] = useState(null);
  const [credSubstep, setCredSubstep] = useState(0); // 0=user/ID, 1=password, 2=access method
  const rafRef = useRef(null);
  const lastAdvanceTsRef = useRef(0);

  // Reset the credentials sub-step whenever we leave step 4.
  useEffect(() => {
    if (step !== 4) setCredSubstep(0);
  }, [step]);

  const def = active ? STEP_DEFS[step] : null;

  // Debounce so a single interaction doesn't advance two steps.
  const safeAdvance = useCallback((delay = 300) => {
    const now = Date.now();
    if (now - lastAdvanceTsRef.current < 600) return;
    lastAdvanceTsRef.current = now;
    setTimeout(() => next(), delay);
  }, [next]);

  // Custom Siguiente handler:
  //  · On credentials step (4) for user/password games, first press moves
  //    from username sub-focus to password sub-focus (no step advance).
  //  · Games with access-method picker (CoD Mobile) add a 3rd sub-step:
  //    user → password → access method.
  //  · If we're leaving credentials on a user/password game, we skip the
  //    "Verify" step (5) entirely because that button doesn't exist.
  const handleNext = useCallback(() => {
    if (step === 4) {
      const type = detectCredentialType();
      if (type === "userpass_access") {
        if (credSubstep === 0) { setCredSubstep(1); return; }
        if (credSubstep === 1) { setCredSubstep(2); return; }
        // credSubstep === 2 → jump to step 6, skipping verify.
        goto(6);
        return;
      }
      if (type === "userpass") {
        if (credSubstep === 0) { setCredSubstep(1); return; }
        // Sub-step 1 finished → jump straight to step 6 (add-to-cart),
        // bypassing the verify step which is irrelevant for this game type.
        goto(6);
        return;
      }
    }
    next();
  }, [step, credSubstep, next, goto]);

  // Custom Atrás handler: mirrors the sub-step logic on step 4 and, when
  // returning from step 6 on a user/pass game, jumps back to the last sub-step
  // of step 4 instead of the (skipped) verify step.
  const handleBack = useCallback(() => {
    if (step === 4 && credSubstep > 0) { setCredSubstep((s) => s - 1); return; }
    if (step === 6) {
      const type = detectCredentialType();
      if (type === "userpass_access") { setCredSubstep(2); goto(4); return; }
      if (type === "userpass") { setCredSubstep(1); goto(4); return; }
    }
    back();
  }, [step, credSubstep, back, goto]);

  // Conditional steps: poll for the target; if it never shows up, auto-skip.
  // Also: on the verify/ff-confirm steps, if the current game is user/password
  // (no ID lookup at all), skip immediately — these steps don't apply.
  useEffect(() => {
    if (!active || !def || !def.conditional) return undefined;
    if (def.key === "verify" || def.key === "ff-confirm") {
      const type = detectCredentialType();
      if (type === "userpass" || type === "userpass_access") {
        next();
        return undefined;
      }
    }
    if (!def.targetSel) return undefined;
    let cancelled = false;
    const startTs = Date.now();
    // Most conditional targets should be in the DOM within a couple hundred
    // ms of the previous step advancing. The FF confirmation box, however,
    // only appears AFTER the upstream nickname lookup finishes (Smile.one
    // typically 2-3s). Give that step a longer window before auto-skipping.
    const timeoutMs = def.key === "ff-confirm" ? 8000 : 1200;
    const iv = setInterval(() => {
      if (cancelled) return;
      const el = document.querySelector(def.targetSel);
      if (el) { clearInterval(iv); return; }
      if (Date.now() - startTs > timeoutMs) { clearInterval(iv); next(); }
    }, 200);
    return () => { cancelled = true; clearInterval(iv); };
  }, [active, def, next]);

  // Compute an "effective def" for the current step. Step 4 (credentials) is
  // dynamic: it targets either the username/ID input, the password input, or
  // the access-method group depending on the game type and the internal
  // sub-step counter.
  const effectiveDef = (() => {
    if (!def) return null;
    if (def.key !== "credentials") return def;
    const type = detectCredentialType();
    if (type === "userpass_access") {
      if (credSubstep === 2) {
        return {
          ...def,
          targetSel: '[data-testid="access-method-group"]',
          title: "Paso 4 · Elige tu Forma de Acceso",
          body: "Selecciona con qué proveedor inicias sesión (Activision, Google (correo) o Facebook). Luego pulsa Siguiente.",
        };
      }
      if (credSubstep === 1) {
        return {
          ...def,
          targetSel: '[data-testid="player-password-input"]',
          title: "Paso 4 · Coloca tu contraseña",
          body: "Ingresa la contraseña de tu cuenta. Cuando termines, pulsa Siguiente para elegir tu Forma de Acceso.",
        };
      }
      return {
        ...def,
        targetSel: '[data-testid="player-id-input"]',
        title: "Paso 4 · Coloca tu usuario",
        body: "Ingresa tu usuario o correo. Cuando termines, pulsa Siguiente para colocar la contraseña.",
      };
    }
    if (type === "userpass") {
      if (credSubstep === 1) {
        return {
          ...def,
          targetSel: '[data-testid="player-password-input"]',
          title: "Paso 4 · Coloca tu contraseña",
          body: "Ahora ingresa la contraseña de tu cuenta. Cuando termines, pulsa Siguiente.",
        };
      }
      return {
        ...def,
        targetSel: '[data-testid="player-id-input"]',
        title: "Paso 4 · Coloca tu usuario",
        body: "Ingresa tu usuario o correo. Cuando termines, pulsa Siguiente para colocar la contraseña.",
      };
    }
    // ID-only or ID+Server games
    return {
      ...def,
      targetSel: '[data-testid="player-id-input"]',
      title: "Paso 4 · Coloca tus credenciales",
      body: "Ingresa tu ID de jugador. Cuando termines, pulsa Siguiente.",
    };
  })();

  // Track the target element rect (rAF loop) for the spotlight.
  useEffect(() => {
    if (!active || !effectiveDef || !effectiveDef.targetSel) { setRect(null); return undefined; }
    let cancelled = false;
    let scrolledOnce = false;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(effectiveDef.targetSel);
      if (el) {
        if (!scrolledOnce) {
          scrolledOnce = true;
          try {
            const r = el.getBoundingClientRect();
            const outOfView = r.top < 60 || r.bottom > (window.innerHeight - 240);
            if (outOfView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch (e) { console.debug("TourOverlay: scrollIntoView failed:", e); }
        }
        const r = el.getBoundingClientRect();
        setRect((prev) => {
          if (prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5
              && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5) {
            return prev;
          }
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      } else {
        setRect(null);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelled = true; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, effectiveDef, step, credSubstep]);

  const clearPulse = useCallback(() => {
    document.querySelectorAll(`.${PULSE_CLASS}`).forEach((el) => el.classList.remove(PULSE_CLASS));
  }, []);

  // Delegated auto-advance listeners for each step's expected action.
  useEffect(() => {
    if (!active || !def) return undefined;
    const handlers = [];
    const listen = (ev, fn, opts) => {
      document.addEventListener(ev, fn, opts);
      handlers.push([ev, fn, opts]);
    };

    switch (def.advanceOn) {
      case "game-click":
        listen("click", (e) => {
          const card = e.target.closest('[data-testid^="game-card-"]');
          if (card && card.getAttribute("aria-disabled") !== "true") safeAdvance(280);
        }, true);
        break;
      case "package-select":
        listen("click", (e) => {
          if (e.target.closest('[data-testid^="package-select-btn-"], [data-testid^="package-card-"]')) safeAdvance(280);
        }, true);
        break;
      case "realizar-pago-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="realizar-pago-btn"]')) safeAdvance(500);
        }, true);
        break;
      case "verify-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="verify-player-id-btn"]')) safeAdvance(500);
        }, true);
        break;
      case "ff-confirm-accept-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="ff-confirm-accept-btn"]')) safeAdvance(400);
        }, true);
        break;
      case "add-to-cart-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="add-to-cart-btn"]')) safeAdvance(400);
        }, true);
        break;
      case "cart-tab-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="client-tab-cart"]')) safeAdvance(280);
        }, true);
        break;
      case "checkout-btn-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="checkout-btn"]')) safeAdvance(500);
        }, true);
        break;
      case "payment-selected":
        listen("change", (e) => {
          if (e.target?.matches?.('[data-testid="payment-method-select"]')
              || e.target?.matches?.('input[type="radio"]')) safeAdvance(300);
        }, true);
        listen("click", (e) => {
          if (e.target.closest('[data-testid="checkout-pm-tabs"] button')
              || e.target.closest('[data-testid^="pm-tab-"]')) safeAdvance(300);
        }, true);
        break;
      case "proof-attached":
        listen("change", (e) => {
          const t = e.target;
          if (t?.matches?.('[data-testid="proof-file-input"]') && t.files?.length >= 1) {
            const submit = document.querySelector('[data-testid="confirm-payment-submit-btn"]');
            submit?.classList?.add(PULSE_CLASS);
            safeAdvance(500);
          }
        }, true);
        break;
      case "confirm-click":
        listen("click", (e) => {
          if (e.target.closest('[data-testid="confirm-payment-submit-btn"]')) {
            clearPulse();
            safeAdvance(700);
          }
        }, true);
        break;
      case "manual-only":
      case "terminal":
      default:
        break;
    }

    return () => {
      handlers.forEach(([ev, fn, opts]) => document.removeEventListener(ev, fn, opts));
    };
  }, [active, def, safeAdvance, clearPulse]);

  // Auto-close terminal after 7s.
  useEffect(() => {
    if (!active) { clearPulse(); return undefined; }
    if (step === TERMINAL_STEP) {
      const t = setTimeout(() => finish(), 7000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, step, finish, clearPulse]);

  if (!active || !def || !effectiveDef) return null;

  // Resolve tooltip title/body from the effective (possibly dynamic) def.
  const title = effectiveDef.title;
  const body = effectiveDef.body;

  // On step 4 with user/password games we can go "back" to the username
  // sub-step, so re-enable the Back button in that case.
  const canGoBackExtra = step === 4 && credSubstep === 1;

  // Spotlight style.
  const spotStyle = rect
    ? {
        position: "fixed",
        top: rect.top - SPOT_PAD,
        left: rect.left - SPOT_PAD,
        width: rect.width + SPOT_PAD * 2,
        height: rect.height + SPOT_PAD * 2,
        borderRadius: 16,
        boxShadow:
          "0 0 0 9999px rgba(0,0,0,0.72), 0 0 0 3px rgba(34,211,238,0.95), 0 0 30px 8px rgba(34,211,238,0.55)",
        transition: "top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease",
        pointerEvents: "none",
        zIndex: 90,
      }
    : {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)", pointerEvents: "none", zIndex: 90,
      };

  // Tooltip position.
  let tooltipStyle;
  if (rect) {
    const w = Math.min(320, window.innerWidth - 32);
    const below = rect.top + rect.height + 20;
    const spaceBelow = window.innerHeight - below;
    const top = spaceBelow > 220 ? below : Math.max(16, rect.top - 240);
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - w - 16));
    tooltipStyle = { position: "fixed", top, left, width: w, zIndex: 92 };
  } else {
    tooltipStyle = {
      position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
      width: Math.min(360, window.innerWidth - 32), zIndex: 92,
    };
  }

  const isTerminal = step === TERMINAL_STEP;
  const canGoBack = (step > 1 && !isTerminal) || canGoBackExtra;

  return createPortal(
    <>
      <style>{`
        @keyframes ag7-tour-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.65), 0 0 0 3px rgba(34,211,238,0.9); }
          50%     { box-shadow: 0 0 0 16px rgba(34,211,238,0),   0 0 0 3px rgba(34,211,238,0.6); }
        }
        .${PULSE_CLASS} { animation: ag7-tour-pulse 1.4s ease-out infinite; }
      `}</style>
      <div data-testid="tour-overlay" style={spotStyle} />
      <div
        data-testid={`tour-tooltip-step-${step}`}
        style={tooltipStyle}
        className="rounded-2xl bg-[#0d1117] border-2 border-cyan-400/70 shadow-[0_0_40px_-4px_rgba(34,211,238,0.55)] p-4"
      >
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            {isTerminal ? <CheckCircle2 className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 leading-none">
              Guía · Paso {isTerminal ? TOTAL_STEPS : step}/{TOTAL_STEPS}
            </p>
            <h4 className="text-sm font-black text-white mt-1">{title}</h4>
            {body && <p className="text-[12px] text-zinc-300 leading-snug mt-1">{body}</p>}
          </div>
          <button
            onClick={skip}
            data-testid="tour-skip-btn"
            aria-label="Cerrar guía"
            className="text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 p-1.5 rounded-full flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-3 flex gap-0.5">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
            <span
              key={n}
              className={`h-1 flex-1 rounded-full transition-all ${n <= (isTerminal ? TOTAL_STEPS : step) ? "bg-cyan-400" : "bg-zinc-700"}`}
            />
          ))}
        </div>

        {/* Nav buttons — every step has Back + Next (except terminal which has "Cerrar") */}
        <div className="mt-3 flex items-center justify-between gap-2">
          {isTerminal ? (
            <button
              onClick={finish}
              data-testid="tour-finish-btn"
              className="flex-1 px-3 py-2 rounded-lg text-[11px] font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white whitespace-nowrap"
            >
              Cerrar guía
            </button>
          ) : (
            <>
              <button
                onClick={handleBack}
                disabled={!canGoBack}
                data-testid="tour-back-btn"
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all ${
                  canGoBack
                    ? "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                    : "bg-zinc-900 text-zinc-600 cursor-not-allowed"
                }`}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Atrás
              </button>
              <button
                onClick={skip}
                data-testid="tour-skip-inline-btn"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white whitespace-nowrap"
              >
                Saltar
              </button>
              <button
                onClick={handleNext}
                data-testid="tour-next-btn"
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-bold bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-md shadow-cyan-500/30 whitespace-nowrap"
              >
                Siguiente
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
